"use client";

import { CarouselCentered } from "@/components/carouselCentered/carouselCentered.component";
import { ProductCard } from "@/components/productCard/productCard.component";
import { Text } from "@/components/text/text.component";

import { brown } from "@/app/font";
import { SHOP_PAGE } from "@/constants/page.constants";
import type { RichText as RichTextType } from "@/sanity.types";

export const RecommendedProducts = ({ block }: RecommendedProductsProps) => {
  if (!block?.products?.length) return null;

  function renderProducts() {
    return (
      <CarouselCentered
        className="container mx-auto"
        ctaLabel={block.ctaLabel ?? "Shop All Products"}
        ctaHref={block.ctaLink?.url ?? SHOP_PAGE}
        ctaClassName={brown.className}>
        {block.products!.map((item, index) => {
          const { product } = item;

          if (!product) return null;

          return (
            <ProductCard
              key={item._key ?? product._id}
              product={{ ...product, id: product.id, legal: item.legal }}
              badges={product.chips ?? undefined}
              index={index}
            />
          );
        })}
      </CarouselCentered>
    );
  }

  return (
    <section
      data-testid="recommended-products"
      className="bg-greyscale-background text-greyscale-headline flex w-full flex-col items-center justify-center gap-8 py-14">
      <div className="flex flex-col gap-2 px-4 text-center">
        <Text variant="h6" component="h5" className="!text-[20px] uppercase">
          {block.sectionLabel}
        </Text>
        <Text
          variant="h3"
          component="h2"
          className="max-w-sm text-center md:max-w-full">
          {block.title}
        </Text>
        {block.description && (
          <Text variant="body1" className="text-greyscale-body">
            {block.description}
          </Text>
        )}
      </div>

      {renderProducts()}
    </section>
  );
};

type RecommendedProductItem = {
  _key?: string;
  legal?: RichTextType | null;
  product?: RecommendedProduct | null;
};

type RecommendedProduct = {
  _id: string;
  id?: string;
  name: string;
  slug: string;
  listPrice?: number | null;
  salesPrice?: number | null;
  monitoringPlan?: {
    id?: string | null;
    name?: string | null;
    listPrice?: number | null;
    salesPrice?: number | null;
  } | null;
  shortDescription?: string | null;
  chips?: string[] | null;
  featuredImage?: { url: string | null; alt: string | null } | null;
  type?: string | null;
};

type RecommendedProductsBlock = {
  _type: "recommendedProducts";
  _key?: string;
  sectionLabel?: string | null;
  title?: string | null;
  description?: string | null;
  ctaLabel?: string | null;
  ctaLink?: { url?: string | null } | null;
  products?: RecommendedProductItem[];
};

export type RecommendedProductsProps = {
  block: RecommendedProductsBlock;
};
